import * as React from "react";
import { cn } from "@/lib/utils";

export type StatusType =
  | "completed"
  | "in_progress"
  | "invited"
  | "pending"
  | "active"
  | "draft"
  | "paused"
  | "expired";

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: string;
  capitalize?: boolean;
}

const statusStyles: Record<string, string> = {
  completed: "bg-green-50 text-green-700",
  published: "bg-green-50 text-green-700",
  in_progress: "bg-blue-50 text-blue-800",
  invited: "bg-yellow-50 text-yellow-800",
  pending: "bg-yellow-50 text-yellow-800",
  active: "bg-green-50 text-green-700",
  draft: "bg-gray-100 text-gray-800",
  paused: "bg-orange-50 text-orange-700",
  expired: "bg-red-50 text-red-700",
};

const getDisplayText = (status: string, capitalize: boolean): string => {
  // Replace underscores with spaces
  const formatted = status.replace(/_/g, " ");

  // Apply capitalization if requested
  return capitalize
    ? formatted.charAt(0).toUpperCase() + formatted.slice(1)
    : formatted;
};

export function StatusBadge({
  status,
  className,
  capitalize = true,
  ...props
}: StatusBadgeProps) {
  if (!status) return null;

  const normalizedStatus = status.toLowerCase();
  const style =
    statusStyles[normalizedStatus] ||
    "bg-gray-100 text-gray-800 ring-1 ring-inset ring-gray-600/20";
  const displayText = getDisplayText(status, capitalize);

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
        style,
        className
      )}
      {...props}
    >
      {displayText}
    </span>
  );
}
